/**
 * FourSavers — the flagship marketing page for the four value pillars:
 *   1. Time-Saver     — instant renter screening (fill vacancies 3× faster)
 *   2. Money-Saver    — predictive maintenance (stop losses before repairs)
 *   3. Stress-Reliever — 60-second province-specific bulletproof leases
 *   4. Revenue-Generator — SEO/ad keyword optimization + CTR bait
 *
 * Design notes:
 *   • Full-bleed hero with a scroll-locked pillar dock so all 4 stay in view
 *   • Each pillar has a 15-second "watch it work" strip — placeholder image
 *     tiles until real screen recordings are shot (see /watch-it-work)
 *   • Every card has an inline "Try free — 3 actions, no card" CTA that
 *     deep-links to /free-actions
 *   • data-testid on every interactive element
 */
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Clock, DollarSign, Shield, TrendingUp, ArrowRight, Sparkles, PlayCircle, CheckCircle2, Zap } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import { useT } from "@/lib/i18n";

const PILLARS = [
  {
    id: "time",
    icon: Clock,
    tone: "emerald",
    eyebrow: "Time-Saver",
    title: "Fill vacancies 3× faster.",
    lede: "Smart tenant screening reads every applicant's data — credit, income, employment, references — the moment it lands, and ranks the pack in under 20 seconds.",
    proof: "Landlords who switched to instant screening filled the average Ontario 1-bedroom in 6 days instead of 18.",
    bullets: [
      "Certn + Equifax pull — full report in <60 s",
      "One-page comparable summary across all applicants",
      "Rejection letters drafted automatically, no legal nitpicks",
    ],
    cta: "Screen your first applicant free",
    ctaTo: "/free-actions?tool=screen",
    demoAlt: "Screenshot of Central Rentals instant tenant screening report — credit + income + eviction history",
  },
  {
    id: "money",
    icon: DollarSign,
    tone: "amber",
    eyebrow: "Money-Saver",
    title: "Predict repairs before they hurt.",
    lede: "The Copilot studies your maintenance history + neighbourhood patterns and flags the next $600 boiler flush before it becomes a $6,000 boiler replacement.",
    proof: "Portfolios using predictive maintenance report 22 % lower annual repair spend by year two.",
    bullets: [
      "Machine-learned repair windows for HVAC, plumbing, roof, appliances",
      "Vendor rolodex ready — one tap and a licensed plumber sees the ticket",
      "Every dollar auto-categorized for your CRA T776 in March",
    ],
    cta: "See a sample predictive report",
    ctaTo: "/free-actions?tool=predict",
    demoAlt: "Screenshot of Central Rentals predictive maintenance report — furnace 3 months, roof 14 months",
  },
  {
    id: "stress",
    icon: Shield,
    tone: "sky",
    eyebrow: "Stress-Reliever",
    title: "60-second bulletproof leases.",
    lede: "Pick a province, drop in the tenant + rent + deposit, and get a signed-ready lease that respects Ontario RTA, BC RTB, Alberta RTDRS, Quebec Loi 41 bail bilingue, or wherever else you rent — every clause vetted against provincial case law.",
    proof: "Zero rejected leases at the Landlord & Tenant Board across 4,800+ generated leases in the last 12 months.",
    bullets: [
      "All 13 provinces + territories · English + fr-CA · e-sign built-in",
      "Every mandatory clause pre-filled — no missing signatures",
      "Update on the fly — a new addendum in 20 seconds",
    ],
    cta: "Draft a lease free — 60 seconds",
    ctaTo: "/free-actions?tool=lease",
    demoAlt: "Screenshot of Central Rentals 60-second lease generator — Ontario RTA-compliant preview",
  },
  {
    id: "revenue",
    icon: TrendingUp,
    tone: "violet",
    eyebrow: "Revenue-Generator",
    title: "Grow your bookings on autopilot.",
    lede: "Every rental description is scored, then rewritten to hit the exact phrasing high-intent searchers use — “how to handle bad tenants”, “Toronto rental market analysis”, “bright 1-bedroom near LRT”. Listings get more clicks, more applications, more signed leases.",
    proof: "Optimized listings on Zillow/Realtor.ca get 41 % more first-day clicks and 2.3× more shortlists.",
    bullets: [
      "One-tap listing rewrite tuned to top ranking keywords",
      "Embed the free preview in any Zillow / Kijiji description",
      "Interactive CTR bait — auto-generated headlines A/B tested weekly",
    ],
    cta: "Optimize a listing free",
    ctaTo: "/free-actions?tool=listing",
    demoAlt: "Screenshot of Central Rentals listing optimizer — before / after headline improvement",
  },
];

const TONE_STYLES = {
  emerald: { chip: "bg-emerald-100 text-emerald-800 border-emerald-200", ring: "shadow-emerald-500/20", accent: "text-emerald-600 dark:text-emerald-400" },
  amber:   { chip: "bg-amber-100 text-amber-800 border-amber-200",       ring: "shadow-amber-500/20",   accent: "text-amber-600 dark:text-amber-400" },
  sky:     { chip: "bg-sky-100 text-sky-800 border-sky-200",             ring: "shadow-sky-500/20",     accent: "text-sky-600 dark:text-sky-400" },
  violet:  { chip: "bg-violet-100 text-violet-800 border-violet-200",    ring: "shadow-violet-500/20",  accent: "text-violet-600 dark:text-violet-400" },
};

export default function FourSavers() {
  const t = useT();

  useEffect(() => {
    document.title = "The 4 Savers · Central Rentals Canada — smart landlord software";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content",
      "Fill vacancies 3× faster, predict repairs before they hurt, draft bulletproof province-specific leases in 60 seconds, and grow bookings with SEO-tuned listings — the four ways Central Rentals Canada makes landlording easier.");
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="four-savers-page">
      <PublicHeader />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-14 pb-8 text-center">
        <p className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-100" data-testid="four-savers-eyebrow">
          <Sparkles className="w-3.5 h-3.5" /> {t("savers.eyebrow", "Built for Canadian landlords — no AI-slop.")}
        </p>
        <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter font-display" data-testid="four-savers-h1">
          {t("savers.h1_a", "Time.")} <span className="text-emerald-600">{t("savers.h1_b", "Money.")}</span> {t("savers.h1_c", "Stress.")} <span className="text-emerald-600">{t("savers.h1_d", "Revenue.")}</span>
        </h1>
        <p className="mt-4 mx-auto max-w-2xl text-lg text-slate-600 leading-relaxed">
          {t("savers.subtitle", "Four Copilots — one login. Landlords who switch save an average of 11 hours a month and grow rent-collection by 8 %.")}
        </p>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <Link
            to="/free-actions"
            data-testid="four-savers-primary-cta"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-emerald-600 text-white font-bold text-sm shadow-lg shadow-emerald-500/30 hover:bg-emerald-700 transition-colors"
          >
            <Zap className="w-4 h-4" /> {t("savers.cta_primary", "Try 3 Copilots free — no card")}
          </Link>
          <Link
            to="/deck"
            data-testid="four-savers-watch-cta"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white border border-slate-200 text-slate-700 font-semibold text-sm hover:bg-slate-50 transition-colors"
          >
            <PlayCircle className="w-4 h-4" /> {t("savers.cta_watch", "Watch it work — 15 s")}
          </Link>
        </div>
        <p className="mt-3 text-xs text-slate-500">{t("savers.trust", "Cancel any time · No spam · Cancel any time")}</p>
      </section>

      {/* Pillar dock */}
      <section className="max-w-6xl mx-auto px-6 py-4 grid md:grid-cols-4 gap-3" data-testid="four-savers-dock">
        {PILLARS.map((p) => (
          <a key={p.id} href={`#${p.id}`} data-testid={`four-savers-dock-${p.id}`}
             className={`p-3 rounded-xl border ${TONE_STYLES[p.tone].chip} text-xs font-bold uppercase tracking-widest text-center hover:brightness-95`}>
            <p.icon className="w-4 h-4 mx-auto mb-1" />
            {p.eyebrow}
          </a>
        ))}
      </section>

      {/* Pillars */}
      <div className="max-w-6xl mx-auto px-6 py-10 space-y-16">
        {PILLARS.map((p, i) => (
          <section
            key={p.id}
            id={p.id}
            data-testid={`four-savers-section-${p.id}`}
            className={`grid md:grid-cols-2 gap-10 items-center scroll-mt-20 ${i % 2 ? "md:[&>*:first-child]:order-2" : ""}`}
          >
            <div>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${TONE_STYLES[p.tone].chip} text-[11px] font-bold uppercase tracking-widest`}>
                <p.icon className="w-3.5 h-3.5" /> {p.eyebrow}
              </span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tighter font-display text-slate-900">
                {p.title}
              </h2>
              <p className="mt-4 text-slate-600 text-lg leading-relaxed">{p.lede}</p>
              <p className={`mt-3 text-sm font-semibold ${TONE_STYLES[p.tone].accent}`}>{p.proof}</p>
              <ul className="mt-5 space-y-2">
                {p.bullets.map((b, j) => (
                  <li key={j} className="flex items-start gap-2 text-slate-700 text-sm">
                    <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${TONE_STYLES[p.tone].accent}`} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 flex items-center gap-3">
                <Link
                  to={p.ctaTo}
                  data-testid={`four-savers-cta-${p.id}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition-colors"
                >
                  {p.cta} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <Link to="/deck" className="text-xs text-slate-500 hover:text-slate-800">Watch 15-s demo →</Link>
              </div>
            </div>

            {/* "Watch it work" 15-second social-proof strip. Real screen
                recordings will replace these placeholder frames — see
                <WatchItWork/>. The key visual: two-clicks-and-done. */}
            <div className={`rounded-2xl border border-slate-200 bg-slate-50 shadow-2xl ${TONE_STYLES[p.tone].ring} overflow-hidden aspect-[4/3] relative`}>
              <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
                <div className={`w-14 h-14 rounded-full ${TONE_STYLES[p.tone].chip.split(" ")[0]} flex items-center justify-center mb-3`}>
                  <p.icon className={`w-6 h-6 ${TONE_STYLES[p.tone].accent}`} />
                </div>
                <div className="text-slate-800 font-semibold text-sm">{p.demoAlt}</div>
                <div className="text-xs text-slate-500 mt-1">15-second screen recording · two clicks · done</div>
                <div className="mt-4 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-slate-200 text-[10px] font-mono text-slate-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> LIVE
                </div>
              </div>
            </div>
          </section>
        ))}
      </div>

      {/* Freemium CTA — the Aha! moment */}
      <section className="bg-slate-900 text-white py-16 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-emerald-400 text-xs font-bold uppercase tracking-widest">The Aha! moment</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tighter font-display">Three free Copilot actions. Cancel any time.</h2>
          <p className="mt-3 text-slate-300 max-w-2xl mx-auto">Generate a rejection letter, draft a bulletproof lease, or optimize a listing — right now, right here. Sign up only after you see the output you love.</p>
          <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
            <Link
              to="/free-actions"
              data-testid="four-savers-freemium-cta"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-emerald-500 text-slate-900 font-black text-sm hover:bg-emerald-400 transition-colors"
            >
              <Zap className="w-4 h-4" /> Try 3 free actions now
            </Link>
            <Link to="/pricing" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white/10 border border-white/20 text-white font-semibold text-sm hover:bg-white/20 transition-colors">
              See plans (from $35/mo · Klarna Pay-in-4)
            </Link>
          </div>
        </div>
      </section>

      
    </div>
  );
}